import parsePhoneNumberFromString from "libphonenumber-js";
import {
  getCountries,
  getCountryCallingCode,
  formatIncompletePhoneNumber,
  parseIncompletePhoneNumber,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
} from "libphonenumber-js";

/** Regional-indicator flag emoji from ISO 3166-1 alpha-2 (e.g. DE → 🇩🇪). */
export function flagEmoji(iso2) {
  const c = String(iso2 || "").toUpperCase();
  if (c.length !== 2) {
    return "🌐";
  }
  return String.fromCodePoint(...[...c].map((ch) => 127397 + ch.charCodeAt(0)));
}

export function formatNationalDisplay(digits, countryIso) {
  const iso = String(countryIso || "DE").toUpperCase();
  const d = String(digits || "").replace(/\D/g, "");
  if (!d) {
    return "";
  }
  return formatIncompletePhoneNumber(d, iso);
}

export function nationalDigitsFromInput(text) {
  return parseIncompletePhoneNumber(text || "");
}

export function nationalPlaceholder(countryIso) {
  const iso = String(countryIso || "DE").toUpperCase();
  const z = "0".repeat(14);
  return formatIncompletePhoneNumber(z, iso);
}

export function isNationalNumberValid(digits, countryIso) {
  const d = String(digits || "").replace(/\D/g, "");
  if (!d) {
    return false;
  }
  const iso = String(countryIso || "DE").toUpperCase();
  const safeIso = getCountries().includes(iso) ? iso : "DE";
  try {
    if (isValidPhoneNumber(d, safeIso)) {
      return true;
    }
    const cc = getCountryCallingCode(safeIso);
    return isValidPhoneNumber(`+${cc}${d}`);
  } catch {
    return false;
  }
}

export function isNationalNumberPossible(digits, countryIso) {
  const d = String(digits || "").replace(/\D/g, "");
  if (!d) {
    return true;
  }
  try {
    return isPossiblePhoneNumber(d, countryIso);
  } catch {
    return false;
  }
}

/**
 * Canonical DB format: "+{callingCode} (0) {nationalNumber}" (national digits only, no leading 0).
 */
export function formatPhoneForDb(countryIso, nationalDigits) {
  const digits = String(nationalDigits || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) {
    return "";
  }
  const iso = String(countryIso || "DE").toUpperCase();
  const safeIso = getCountries().includes(iso) ? iso : "DE";
  const cc = getCountryCallingCode(safeIso);
  return `+${cc} (0) ${digits}`;
}

export function parsePhoneFromDb(stored, defaultCountry = "DE") {
  if (!stored || !String(stored).trim()) {
    return { country: defaultCountry, national: "" };
  }
  const s = String(stored).trim();
  const paren = s.match(/^\+\s*(\d{1,4})\s*\(0\)\s*([\d\s\-]+)$/);
  if (paren) {
    const dial = paren[1];
    const national = paren[2].replace(/\D/g, "").replace(/^0+/, "");
    const candidates = getCountries().filter((c) => getCountryCallingCode(c) === dial);
    const country = candidates[0] || defaultCountry;
    return { country, national };
  }
  const parsed = parsePhoneNumberFromString(s, defaultCountry);
  if (parsed) {
    const nat = String(parsed.nationalNumber || "").replace(/^0+/, "");
    return { country: parsed.country || defaultCountry, national: nat };
  }
  return { country: defaultCountry, national: s.replace(/\D/g, "") };
}

let cachedOptions;
export function getPhoneCountryOptions() {
  if (cachedOptions) {
    return cachedOptions;
  }
  let dn;
  try {
    dn = new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    dn = null;
  }
  cachedOptions = getCountries()
    .map((code) => ({
      code,
      dial: getCountryCallingCode(code),
      name: (dn && dn.of(code)) || code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return cachedOptions;
}

export function findCountryOption(options, countryIso) {
  if (!countryIso) {
    return null;
  }
  const upper = String(countryIso).toUpperCase();
  return options.find((o) => o.code === upper) || null;
}
